import { useCallback, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Controlled text input for substring-searching the dashboard project list
 * by name (case-insensitive contains match).
 *
 * Owns nothing but its render. The parent (HomePage) holds the input value
 * in state and debounces the URL/query write, so this component stays pure
 * and easily testable. Styles mirror DashboardTabBar.tsx for visual parity
 * with the rest of the dashboard chrome.
 *
 * Clear-button visibility: the `×` only renders when `value` is non-empty so
 * the empty state has nothing to dismiss. Clicking it calls `onChange('')`
 * — the parent then debounces the same way as any other change so the URL
 * and refetch sync stays single-pathed.
 */
export interface DashboardSearchInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

export default function DashboardSearchInput({
  value,
  onChange,
  placeholder,
}: DashboardSearchInputProps) {
  const { t } = useTranslation('settings')

  const handleInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value)
    },
    [onChange],
  )

  const handleClear = useCallback(() => {
    onChange('')
  }, [onChange])

  const hasValue = value.length > 0

  return (
    <div className="mb-4 flex items-center gap-2" data-testid="dashboard-search">
      <div className="relative flex-1 max-w-md">
        <span
          className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
          aria-hidden="true"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-4.35-4.35M11 19a8 8 0 110-16 8 8 0 010 16z"
            />
          </svg>
        </span>
        <input
          type="text"
          value={value}
          onChange={handleInput}
          placeholder={placeholder ?? t('search.placeholder')}
          aria-label={t('search.ariaLabel')}
          className="w-full pl-9 pr-9 py-2 text-sm bg-surface-1 border border-border rounded-md text-foreground placeholder:text-text-muted focus:outline-none focus:border-primary/60 transition-colors"
          data-testid="dashboard-search-input"
        />
        {hasValue && (
          <button
            type="button"
            onClick={handleClear}
            aria-label={t('search.clearLabel')}
            title={t('search.clearLabel')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-text-muted hover:text-foreground hover:bg-surface-2 transition-colors"
            data-testid="dashboard-search-clear"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}
